import { useState, type JSX } from 'react';
import { z } from 'zod';
import { type BotScope } from '@cryptology-assist/domain';
import { request, botPath, type Session } from './api';
import { Field, ErrorNotice, onSubmit, useMutation } from './components';
import type { Copy } from './copy';

const previewSchema = z.object({
  sessionId: z.string().uuid(),
  text: z.string(),
  history: z.array(
    z.object({
      id: z.string(),
      role: z.enum(['customer', 'assistant']),
      text: z.string(),
      occurredAt: z.string(),
    })
  ),
  proposedAction: z.object({ kind: z.string() }).passthrough().nullable(),
});
export function ConfigurationPreview({
  scope,
  revision,
  session,
  copy,
  channels,
}: {
  scope: BotScope;
  revision: string;
  session: Session;
  copy: Copy;
  channels: ('chat' | 'email')[];
}): JSX.Element {
  const [result, setResult] = useState<z.infer<typeof previewSchema> | null>(
    null
  );
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState<'chat' | 'email'>(
    channels[0] ?? 'chat'
  );
  const mutation = useMutation();
  const stopped = Boolean(
    result?.proposedAction || (result && result.history.length >= 20)
  );
  return (
    <section className="form-section">
      <h2>{copy.configurationPreview}</h2>
      <p>{copy.configurationPreviewHelp}</p>
      <form
        onSubmit={(event) =>
          onSubmit(event, () =>
            mutation.run(async () => {
              setResult(
                await request(
                  botPath(scope, 'configuration/preview'),
                  previewSchema,
                  {
                    csrf: session.csrf,
                    body: {
                      revision,
                      sessionId: result?.sessionId ?? null,
                      channel,
                      message,
                    },
                  }
                )
              );
              setMessage('');
            })
          )
        }
      >
        <fieldset disabled={mutation.busy || !channels.length}>
          <Field label={copy.channels}>
            <select
              value={channel}
              onChange={(event) => {
                setChannel(event.target.value as 'chat' | 'email');
                setResult(null);
              }}
            >
              {channels.map((value) => (
                <option key={value} value={value}>
                  {copy[value]}
                </option>
              ))}
            </select>
          </Field>
          <div aria-live="polite">
            {result?.history.map((item) => (
              <p className="preview-message" key={item.id}>
                <strong>
                  {item.role === 'customer' ? copy.previewCustomer : copy.name}
                </strong>
                <br />
                {item.text}
              </p>
            ))}
          </div>
          {result?.proposedAction && (
            <p role="status">{copy.previewActionBoundary}</p>
          )}
          <Field label={copy.previewMessage}>
            <textarea
              required
              maxLength={4000}
              value={message}
              disabled={stopped}
              onChange={(event) => setMessage(event.target.value)}
            />
          </Field>
          <button disabled={stopped || !message.trim()}>
            {copy.previewSend}
          </button>
          <button
            type="button"
            onClick={() => {
              setResult(null);
              setMessage('');
            }}
          >
            {copy.previewReset}
          </button>
        </fieldset>
        <ErrorNotice error={mutation.error} copy={copy} />
      </form>
    </section>
  );
}
